import { motion } from "framer-motion";
import { ShieldCheck, FileText, CreditCard, Scale, Cpu, Cloud, Code2, Globe } from "lucide-react";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import CTABand from "@/components/CTABand";

const ease = [0.16, 1, 0.3, 1];

const GAP = [
  {
    name: "Ticketmaster & Live Nation",
    verdict: "Dominant but bloated.",
    detail: "Poor UX for spontaneous discovery. No local community layer.",
  },
  {
    name: "SeatGeek & Eventbrite",
    verdict: "Broader reach but generic.",
    detail: "Weak hyper-local curation and limited social integration.",
  },
  {
    name: "The Market Gap",
    verdict: "Nobody owns the moment.",
    detail: "No platform owns spontaneous, social-first live event discovery in dense urban markets like Boston. We partner independents, out-localize incumbents, and own curated discovery.",
    highlight: true,
  },
];

const PHASES = [
  { num: "01", name: "Beta Launch", months: "Months 4-6", desc: "Soft beta release with limited venues, influencer onboarding, and test ticketing to validate product-market fit." },
  { num: "02", name: "Growth & Scale", months: "Months 7-12", desc: "Expand event inventory, launch paid marketing campaigns, and build promoter partnerships across Boston." },
  { num: "03", name: "Market Dominance", months: "Months 10-12", desc: "Optimize pricing, pursue seed funding, scale infrastructure, and begin pilot expansion to new cities, starting with Trinidad & Tobago." },
];

const LEGAL = [
  { icon: Scale, title: "Business Entity Registration", desc: "Incorporate as a Delaware C-Corp for investor readiness, file as a foreign entity in Massachusetts, and obtain an EIN with a dedicated business bank account." },
  { icon: FileText, title: "Legal Documentation", desc: "Terms of Service, Privacy Policy, and liability agreements, with GDPR and CCPA compliance for user data protection." },
  { icon: CreditCard, title: "Ticket Resale & Payment Compliance", desc: "Comply with Massachusetts ticket resale laws and consumer protection regulations, with PCI-compliant payment processing and fraud prevention." },
];

const IP = [
  { icon: ShieldCheck, num: "01", title: "USPTO Trademark Filing", desc: "Comprehensive USPTO search and filings under Class 9 (software/apps) and Class 41 (entertainment services)." },
  { icon: Globe, num: "02", title: "Domains & Digital Handles", desc: "All brand domains (.com, .io, .co) and social handles secured across TikTok, Instagram, X, and LinkedIn before public launch." },
  { icon: Cpu, num: "03", title: "App Design & Licensing", desc: "Proprietary UI/UX and brand assets protected, with licensing frameworks for third-party integrations and white-label partnerships." },
];

const STACK = [
  { icon: Code2, name: "React Native", detail: "Cross-platform iOS + Android performance" },
  { icon: CreditCard, name: "Stripe", detail: "PCI-compliant payments & fraud prevention" },
  { icon: Cpu, name: "Node.js / Rails + PostgreSQL", detail: "Robust backend and data layer" },
  { icon: Cloud, name: "AWS", detail: "Cloud hosting & infrastructure at scale" },
];

const MONTHS = [
  { m: "Month 1", t: "Business plan, entity registration, trademark search" },
  { m: "Month 2", t: "Trademark filing, begin development, legal docs drafted" },
  { m: "Month 3", t: "MVP completion, initial venue partnerships, branding prep" },
  { m: "Month 4", t: "Beta launch, venue onboarding, test ticketing live" },
  { m: "Month 5", t: "Product refinement, partnership expansion, influencer campaigns" },
  { m: "Month 6", t: "Soft launch, initial revenue, community growth" },
  { m: "Month 7", t: "Marketing scale-up & premium feature rollout" },
  { m: "Month 8", t: "Corporate partnerships & event inventory expansion" },
  { m: "Month 9", t: "Pricing optimization & Trinidad & Tobago pilot launch" },
  { m: "Month 10", t: "Additional US city pilots & investor outreach" },
  { m: "Month 11", t: "Seed funding close & infrastructure scaling" },
  { m: "Month 12", t: "Full-scale operations & expansion execution" },
];

const Company = () => (
  <div data-testid="page-company" className="pt-28 sm:pt-32">
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>The Company</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-black leading-[1.08] tracking-tight text-charcoal-900 sm:text-4xl lg:text-5xl">
            A technology company. An entertainment company.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-steel sm:text-lg">
            True Life Entertainment is the bridge between entertainment companies and technology.
            Stagehands and engineers build our core products from the inside: LANTIX, the social
            side of ticketing, and LANTIX PRO, labor and production in cities near you. We are
            preparing the next form of the entertainment market and introducing the new form of
            robotics to the industry.
          </p>
        </Reveal>
      </div>
    </section>

    <section data-testid="competitive-gap" className="bg-surface py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Competitive Landscape" title="Why the incumbents leave the door open." />
        <div className="mt-12 space-y-4">
          {GAP.map((g, i) => (
            <Reveal key={g.name} delay={i * 0.06}>
              <div
                data-testid={`gap-row-${i}`}
                className={`flex flex-col justify-between gap-2 border p-7 sm:flex-row sm:items-center sm:gap-8 ${
                  g.highlight ? "border-accent/40 bg-accent-subtle/50" : "border-line bg-white"
                }`}
              >
                <div className="sm:w-64 sm:shrink-0">
                  <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-steel">{g.name}</div>
                  <div className={`mt-1.5 font-display text-lg font-bold ${g.highlight ? "text-accent" : "text-charcoal-900"}`}>{g.verdict}</div>
                </div>
                <p className="max-w-xl text-sm leading-relaxed text-steel">{g.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="rollout-section" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Market Entry" title="A phased path from first beta to market dominance." />
        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
          {PHASES.map((p, i) => (
            <motion.div
              key={p.num}
              data-testid={`phase-${p.num}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.65, delay: i * 0.1, ease }}
              className="flex h-full flex-col border border-line bg-white p-8 transition-colors duration-300 hover:border-accent/50"
            >
              <span className="ghost-num font-display text-6xl font-black">{p.num}</span>
              <h3 className="mt-8 font-display text-xl font-bold text-charcoal-900">{p.name}</h3>
              <p className="mt-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">{p.months}</p>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-steel">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="operating-foundation" className="bg-charcoal-900 py-20 text-white sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead dark eyebrow="Operating Foundation" title="A compliant, protected, modern foundation." lead="From entity formation to intellectual property, the business is built to scale without regulatory risk." />
        <div className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            {LEGAL.map((l, i) => (
              <Reveal key={l.title} delay={i * 0.06}>
                <div data-testid={`legal-item-${i}`} className="flex h-full items-start gap-5 border border-white/10 bg-charcoal-800 p-6">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-violet-400">
                    <l.icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-display text-base font-bold">{l.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{l.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="space-y-4">
            {IP.map((l, i) => (
              <Reveal key={l.title} delay={i * 0.06}>
                <div data-testid={`ip-item-${i}`} className="flex h-full items-start gap-5 border border-white/10 bg-charcoal-800 p-6">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center border border-white/15 text-violet-400">
                    <l.icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-display text-base font-bold">{l.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{l.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.18}>
              <div data-testid="tech-stack-card" className="border border-white/10 bg-charcoal-800 p-6">
                <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-400">Technology</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {STACK.map((s) => (
                    <div key={s.name} className="flex items-center gap-3 border border-white/10 p-3.5">
                      <s.icon className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.8} />
                      <div>
                        <div className="text-sm font-semibold text-white">{s.name}</div>
                        <div className="text-xs text-slate-500">{s.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section data-testid="roadmap-12mo" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="12-Month Roadmap" title="From business plan to full-scale operations in one year." />
        <div className="mt-14 grid grid-cols-1 gap-x-10 md:grid-cols-2">
          {MONTHS.map((mo, i) => (
            <Reveal key={mo.m} delay={(i % 6) * 0.04}>
              <div data-testid={`roadmap-month-${i + 1}`} className="flex items-baseline gap-5 border-b border-line py-5">
                <span className="w-20 shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-accent">{mo.m}</span>
                <span className="text-sm leading-relaxed text-charcoal-900">{mo.t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand testid="company-cta" />
  </div>
);

export default Company;
